import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Container, 
  Box, 
  Typography, 
  Grid, 
  Card, 
  Divider,
  useMediaQuery 
} from '@mui/material';
import { motion } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import StarsIcon from '@mui/icons-material/Stars';
import HistoryIcon from '@mui/icons-material/History';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import GroupsIcon from '@mui/icons-material/Groups';

function AboutUsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };
  
  const fadeInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  // Company features
  const features = [
    {
      icon: <HistoryIcon fontSize="large" sx={{ color: "#db352a" }} />,
      title: "Established 2005",
      description: "Providing exceptional travel services for nearly two decades."
    },
    {
      icon: <LocalOfferIcon fontSize="large" sx={{ color: "#db352a" }} />,
      title: "Competitive Pricing",
      description: "Quality travel experiences at affordable rates for all budgets."
    },
    {
      icon: <DirectionsCarIcon fontSize="large" sx={{ color: "#db352a" }} />,
      title: "Modern Fleet",
      description: "Well-maintained vehicles ensuring comfort and safety."
    },
    {
      icon: <GroupsIcon fontSize="large" sx={{ color: "#db352a" }} />,
      title: "Expert Team",
      description: "Experienced professionals dedicated to exceptional service."
    }
  ];

  return (
    <div style={{ overflow: "hidden" }}>
      {/* Background pattern */}
      <div style={{ 
        position: "fixed", 
        width: "100%", 
        height: "100vh", 
        zIndex: -1,
        backgroundImage: "url('/images/world-map-background.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        opacity: 0.05
      }} />
      
      <Header />

      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 8, md: 12 },
          pb: { xs: 6, md: 8 },
          background: "linear-gradient(to bottom, rgba(219,53,42,0.05) 0%, rgba(255,255,255,1) 100%)",
          position: "relative"
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h2"
                component="h1"
                sx={{ 
                  fontWeight: "bold",
                  color: "#db352a",
                  mb: 2,
                  position: "relative",
                  display: "inline-block",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    width: "60%",
                    height: "4px",
                    backgroundColor: "#db352a",
                    bottom: "-12px",
                    left: "20%"
                  }
                }}
              >
                About Us
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Main content section */}
      <Container maxWidth="lg">
        {/* First section */}
        <Box sx={{ my: 8 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={fadeInLeft}
              >
                <Typography 
                  variant="h4" 
                  gutterBottom 
                  sx={{ 
                    color: "#db352a",
                    fontWeight: "bold",
                    mb: 3
                  }}
                >
                  Your Trusted Travel Partner Since 2005
                </Typography>
                
                <Typography 
                  variant="body1" 
                  paragraph
                  sx={{ 
                    color: "#555",
                    lineHeight: 1.8,
                    mb: 4
                  }}
                >
                  <span style={{ color: "#db352a", fontWeight: "bold" }}>Ashoka Tours and Travels</span>, 
                  based in Aurangabad, has been curating personalized and
                  ready-made tour packages across India since 2005. Our
                  expertise covers a range of travel experiences, from luxury
                  and group tours to adventure and heritage trips, all at
                  competitive prices.
                </Typography>
                
                <Typography 
                  variant="body1" 
                  paragraph
                  sx={{ 
                    color: "#555",
                    lineHeight: 1.8
                  }}
                >
                  With a focus on providing seamless journeys, we prioritize customer service, 
                  cleanliness, and comfort. Backed by an experienced team, a modern fleet of
                  vehicles, and a commitment to excellence, we ensure your
                  travel dreams turn into memorable adventures. Choose us for
                  reliable, local expertise and unparalleled service that puts
                  you first.
                </Typography>
              </motion.div>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={fadeInRight}
              >
                <Box
                  component="img"
                  src={process.env.PUBLIC_URL + "/images/RavirajHotel3.png  "}
                  alt="Ashoka Tours Office"
                  sx={{
                    width: "100%",
                    height: "auto",
                    borderRadius: "16px",
                    boxShadow: "0 15px 30px rgba(0,0,0,0.15)",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.02)"
                    }
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </Box>

        {/* Features section */}
        <Box 
          sx={{ 
            my: 10, 
            pt: 4,
            pb: 6,
            px: { xs: 2, md: 5 },
            background: "linear-gradient(to right, rgba(219,53,42,0.05) 0%, rgba(219,53,42,0.1) 100%)",
            borderRadius: "20px"
          }}
        >
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
          >
            <Typography 
              variant="h4" 
              align="center"
              sx={{ 
                mb: 6,
                color: "#db352a",
                fontWeight: "bold"
              }}
            >
              Why Choose Us
            </Typography>
          </motion.div>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      p: 3,
                      textAlign: "center",
                      borderRadius: "12px",
                      background: "rgba(255,255,255,0.8)",
                      transition: "transform 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 10px 25px rgba(219,53,42,0.15)"
                      }
                    }}
                  >
                    <Box sx={{ mb: 2 }}>
                      {feature.icon}
                    </Box>
                    <Typography 
                      variant="h6" 
                      gutterBottom
                      sx={{ fontWeight: "bold" }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Lonely Planet endorsement */}
        <Box 
          sx={{ 
            my: 10,
            position: "relative",
            overflow: "hidden"
          }}
        >
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
          >
            <Card
              elevation={0}
              sx={{
                position: "relative",
                overflow: "hidden",
                borderRadius: "16px",
                border: "1px solid rgba(0,0,0,0.08)",
                background: "linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%)"
              }}
            >
              <Grid container spacing={0}>
                <Grid item xs={12} md={5}>
                  <Box
                    component="img"
                    src={process.env.PUBLIC_URL + "/images/withcliets.jpeg"}
                    alt="With Clients"
                    sx={{
                      width: "100%",
                      height: { xs: "250px", md: "100%" },
                      objectFit: "cover"
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={7}>
                  <Box sx={{ p: { xs: 3, md: 5 } }}>
                    <Box 
                      sx={{ 
                        display: "flex", 
                        alignItems: "center",
                        mb: 3
                      }}
                    >
                      <StarsIcon sx={{ color: "#db352a", mr: 1, fontSize: "2rem" }} />
                      <Typography 
                        variant="h5" 
                        component="h2"
                        sx={{ fontWeight: "bold" }}
                      >
                        Recommended by <span style={{ color: "#2E64A1" }}>Lonely Planet</span>
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ mb: 3 }} />
                    
                    <Typography 
                      variant="body1" 
                      paragraph
                      sx={{ 
                        lineHeight: 1.8,
                        color: "#555"
                      }}
                    >
                      <span style={{ color: "#db352a", fontWeight: "bold" }}>Ashoka Tours and Travels</span> is
                      proud to be recommended by <span style={{ color: "#2E64A1", fontWeight: "bold" }}>Lonely Planet</span> in their India Trip
                      Guide, a testament to our dedication to delivering exceptional
                      travel experiences.
                    </Typography>
                    
                    <Typography 
                      variant="body1" 
                      paragraph
                      sx={{ 
                        lineHeight: 1.8,
                        color: "#555"
                      }}
                    >
                      This prestigious endorsement highlights our reputation as a trusted tour operator, 
                      offering curated journeys that showcase the best of India's rich culture and
                      history. Whether you're looking for adventure, heritage, or
                      leisure, our personalized services and commitment to customer
                      satisfaction ensure that every trip with us is unforgettable.
                    </Typography>
                    
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontStyle: "italic",
                        color: "#777",
                        mt: 3,
                        fontWeight: "medium"
                      }}
                    >
                      "With this recognition, you can trust us to plan your dream
                      vacation with expertise and care."
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </motion.div>
        </Box>
      </Container>
      
      <Footer />
    </div>
  );
}

export default AboutUsPage;